/**
 * 68 / 63 / dk63Machine / 68EVO / M71 等布局：矩阵第 0 行为占位，Keyboard 会 slice(1) 展示。
 * 视觉 rowIndex 与 KeyConfig.originalRow（固件矩阵行）相差 1。
 */

export const SLICE_FIRST_ROW_KEYBOARD_TYPES = [
  "68",
  "huafenda68",
  "tank68he",
  "63",
  "dk63Machine",
  "68EVO",
  "m71",
] as const;

export type SliceFirstRowKeyboardType =
  (typeof SLICE_FIRST_ROW_KEYBOARD_TYPES)[number];

export const DEEP80MAX_KEYBOARD_TYPE = "deep80max" as const;

export function isDeep80MaxKeyboardType(keyboardType?: string | null): boolean {
  return keyboardType === DEEP80MAX_KEYBOARD_TYPE;
}

export function isX75v3KeyboardType(keyboardType?: string | null): boolean {
  const kt = String(keyboardType ?? "");
  return (
    kt === "x75v3" ||
    kt === "gamma75" ||
    kt === "dk75" ||
    kt === "dk75Pro" ||
    kt === "dk75E"
  );
}

type CalibrationRowLike = {
  route?: Array<number | string | null | undefined>;
  adc?: Array<number | string | null | undefined>;
  status?: Array<number | string | null | undefined>;
  performance?: unknown[];
};

/** Gamma75 Shift 行：紧凑 14 列（无占位列） vs UI/固件 ≥15 列（含 col1 占位） */
const GAMMA75_SHIFT_ROW_COMPACT_LEN = 14;
const GAMMA75_SHIFT_ROW_UI_LEN = 16;

/** SDK 紧凑列 → UI 矩阵列（LShift 后 col1 插入占位列 0） */
function resolveGamma75ShiftRowSdkToUiCol(sdkCol: number): number {
  return sdkCol <= 0 ? sdkCol : sdkCol + 1;
}

/** UI 矩阵列 → SDK 紧凑列 */
function resolveGamma75ShiftRowUiToSdkCol(uiCol: number): number {
  return uiCol >= 2 ? uiCol - 1 : uiCol;
}

function resolveGamma75ShiftRowCalibrationLen(
  sdkRow: CalibrationRowLike
): number {
  return Math.max(
    sdkRow.adc?.length ?? 0,
    sdkRow.route?.length ?? 0,
    sdkRow.status?.length ?? 0,
    sdkRow.performance?.length ?? 0
  );
}

/** SDK 行已与 UI 矩阵同宽（含 LShift 后 col1 占位列），不可再做紧凑→UI 偏移 */
function isGamma75ShiftRowUiAlignedCalibrationRow(
  sdkRow: CalibrationRowLike
): boolean {
  return (
    resolveGamma75ShiftRowCalibrationLen(sdkRow) > GAMMA75_SHIFT_ROW_COMPACT_LEN
  );
}

function padGamma75ShiftRowCalibrationToUi(sdkRow: CalibrationRowLike): {
  adc: number[];
  route: number[];
  status: number[];
  performance: unknown[];
} {
  const adc = Array.from({ length: GAMMA75_SHIFT_ROW_UI_LEN }, (_, i) =>
    Number(sdkRow.adc?.[i] ?? 0)
  );
  const route = Array.from({ length: GAMMA75_SHIFT_ROW_UI_LEN }, (_, i) =>
    Number(sdkRow.route?.[i] ?? 0)
  );
  const status = Array.from({ length: GAMMA75_SHIFT_ROW_UI_LEN }, (_, i) =>
    Number(sdkRow.status?.[i] ?? 0)
  );
  const performance = Array.from(
    { length: GAMMA75_SHIFT_ROW_UI_LEN },
    (_, i) => sdkRow.performance?.[i] ?? 0
  );
  return { adc, route, status, performance };
}

/**
 * Gamma75 Shift 行：SDK 校准数据 → UI 矩阵（LShift 后 col1 占位列）。
 * 固件若已返回 16 列 UI 矩阵则原样对齐；仅紧凑列（无占位列）时才插入 col1。
 */
export function expandGamma75ShiftRowCalibrationToUi(
  row: number,
  sdkRow: CalibrationRowLike
): {
  adc: number[];
  route: number[];
  status: number[];
  performance: unknown[];
} {
  if (row !== 4) {
    return {
      adc: [...(sdkRow.adc ?? [])].map((v) => Number(v) || 0),
      route: [...(sdkRow.route ?? [])].map((v) => Number(v) || 0),
      status: [...(sdkRow.status ?? [])].map((v) => Number(v) || 0),
      performance: [...(sdkRow.performance ?? [])],
    };
  }

  if (isGamma75ShiftRowUiAlignedCalibrationRow(sdkRow)) {
    return padGamma75ShiftRowCalibrationToUi(sdkRow);
  }

  const adc = Array.from({ length: GAMMA75_SHIFT_ROW_UI_LEN }, () => 0);
  const route = Array.from({ length: GAMMA75_SHIFT_ROW_UI_LEN }, () => 0);
  const status = Array.from({ length: GAMMA75_SHIFT_ROW_UI_LEN }, () => 0);
  const performance: unknown[] = Array.from(
    { length: GAMMA75_SHIFT_ROW_UI_LEN },
    () => 0
  );

  const sdkLen = resolveGamma75ShiftRowCalibrationLen(sdkRow);

  for (let sdkCol = 0; sdkCol < sdkLen; sdkCol++) {
    const uiCol = resolveGamma75ShiftRowSdkToUiCol(sdkCol);
    if (uiCol < 0 || uiCol >= GAMMA75_SHIFT_ROW_UI_LEN) continue;
    if (sdkRow.adc?.[sdkCol] != null)
      adc[uiCol] = Number(sdkRow.adc[sdkCol]) || 0;
    if (sdkRow.route?.[sdkCol] != null) {
      route[uiCol] = Number(sdkRow.route[sdkCol]) || 0;
    }
    if (sdkRow.status?.[sdkCol] != null) {
      status[uiCol] = Number(sdkRow.status[sdkCol]) || 0;
    }
    if (sdkRow.performance?.[sdkCol] != null) {
      performance[uiCol] = sdkRow.performance[sdkCol];
    }
  }

  return { adc, route, status, performance };
}

/**
 * Gamma75 Shift 行校准采样列：
 * UI 矩阵含 LShift 后占位列（originalCol）；须与校准数组列宽一致，禁止 keyIndex 回退。
 */
export function resolveGamma75CalibrationSampleCol(
  matrixRow: number,
  matrixCol: number,
  keyIndex: number,
  rowData: CalibrationRowLike | undefined,
  keyboardType?: string | null
): number {
  if (
    (keyboardType !== "gamma75" &&
      keyboardType !== "dk75" &&
      keyboardType !== "dk75Pro" &&
      keyboardType !== "dk75E") ||
    matrixRow !== 4
  ) {
    return matrixCol;
  }
  if (matrixCol < 0) return keyIndex;
  if (isGamma75ShiftRowUiAlignedCalibrationRow(rowData ?? {})) {
    return matrixCol;
  }
  return resolveGamma75ShiftRowUiToSdkCol(matrixCol);
}

export function usesSliceFirstRowLayout(keyboardType?: string | null): boolean {
  const kt = String(keyboardType ?? "").trim();
  if (!kt) return false;
  if (
    SLICE_FIRST_ROW_KEYBOARD_TYPES.includes(kt as SliceFirstRowKeyboardType)
  ) {
    return true;
  }
  /** 兼容调用方 toLowerCase 后的 "68evo"（表内为 "68EVO"） */
  const lower = kt.toLowerCase();
  return SLICE_FIRST_ROW_KEYBOARD_TYPES.some((t) => t.toLowerCase() === lower);
}

type MatrixKeyLike = {
  originalRow?: number;
  row?: number;
  originalCol?: number;
};

/**
 * 业务/SDK 用行号（与触发/高级键 getRowData 入参一致，ES68 等为 slice 后的视觉行 0..n-1）。
 */
export function resolveUiMatrixRow(
  key: MatrixKeyLike,
  keyboardType?: string | null
): number | undefined {
  if (!usesSliceFirstRowLayout(keyboardType)) {
    const r = key.originalRow ?? key.row;
    return r != null && r >= 0 ? r : undefined;
  }
  if (key.row != null && key.row >= 0) return key.row;
  if (key.originalRow != null && key.originalRow >= 0) {
    return key.originalRow > 0 ? key.originalRow - 1 : key.originalRow;
  }
  return undefined;
}

/**
 * 固件矩阵行（灯效、keyboardAll 拉性能、校准批量拉取等）。
 */
export function resolveSdkMatrixRow(
  key: MatrixKeyLike,
  layoutRowIndex?: number,
  keyboardType?: string | null
): number | undefined {
  if (
    !usesSliceFirstRowLayout(keyboardType) &&
    !isX75v3KeyboardType(keyboardType)
  ) {
    const r = key.originalRow ?? key.row ?? layoutRowIndex;
    return r != null && r >= 0 ? r : undefined;
  }
  if (isX75v3KeyboardType(keyboardType)) {
    const r = key.originalRow ?? layoutRowIndex;
    return r != null && r >= 0 ? r : undefined;
  }
  if (key.originalRow != null && key.originalRow >= 0) return key.originalRow;
  if (layoutRowIndex != null && layoutRowIndex >= 0) {
    return layoutRowIndex + 1;
  }
  if (key.row != null && key.row >= 0) return key.row + 1;
  return undefined;
}

/**
 * teampreset / axis 等二维数组的行下标。
 * - 数据来自完整矩阵 layout（行数 = 视觉行 + 1）→ visual + 1
 * - 数据来自已 slice 的 layout（行数 = 视觉行）→ visual
 */
export function resolveLayoutDataRowIndex(
  visualRowIndex: number,
  activeRowCount: number,
  dataRowCount: number
): number {
  if (
    activeRowCount > 0 &&
    dataRowCount > 0 &&
    dataRowCount === activeRowCount + 1
  ) {
    return visualRowIndex + 1;
  }
  return visualRowIndex;
}

/** 校准等：视觉行 → SDK getADCSample/getRoute 的 row 参数 */
export function resolveSdkMatrixRowFromVisual(
  visualRowIndex: number,
  keyboardType?: string | null
): number {
  return usesSliceFirstRowLayout(keyboardType)
    ? visualRowIndex + 1
    : visualRowIndex;
}

export function resolveMatrixColIndex(
  key: MatrixKeyLike,
  keyIndex: number
): number {
  return key.originalCol != null && key.originalCol >= 0
    ? key.originalCol
    : keyIndex;
}

/**
 * keyPositionMap 行号：旧型号用视觉 rowIndex；slice 型号用固件对齐行；deep80max 用 originalRow。
 */
export function resolveKeyPositionMapRow(
  key: MatrixKeyLike,
  layoutRowIndex: number,
  keyboardType?: string | null
): number {
  if (usesSliceFirstRowLayout(keyboardType)) {
    return resolveUiMatrixRow(key, keyboardType) ?? layoutRowIndex;
  }
  if (
    isDeep80MaxKeyboardType(keyboardType) ||
    isX75v3KeyboardType(keyboardType)
  ) {
    if (key.originalRow != null && key.originalRow >= 0) {
      return key.originalRow;
    }
    return layoutRowIndex;
  }
  return layoutRowIndex;
}

/** BAT68 / Tank68 HE：Shift 行 LShift 与 Z 间有矩阵占位列，UI 不渲染但 SDK 数组保留，须用 originalCol */
function usesHfd68ShiftGapOriginalCol(keyboardType?: string | null): boolean {
  return keyboardType === "huafenda68" || keyboardType === "tank68he";
}

/**
 * 测试校准 randomValues：addDelData 删列/插列后数组下标与视觉行 keyIndex 一致（非 originalCol）。
 * 例：ES68 底行删 SDK 列 1 后，Z 的 originalCol=2 但 keyIndex=1。
 * BAT68 / Tank68 HE：Shift 行 UI 不渲染 col1 占位，但 SDK 数组仍保留该列，须用 originalCol。
 */
export function resolveCalibrationDataColIndex(
  key: MatrixKeyLike,
  keyIndex: number,
  keyboardType?: string | null
): number {
  if (usesSliceFirstRowLayout(keyboardType)) {
    if (usesHfd68ShiftGapOriginalCol(keyboardType)) {
      return resolveMatrixColIndex(key, keyIndex);
    }
    return keyIndex;
  }
  if (isDeep80MaxKeyboardType(keyboardType)) {
    return resolveMatrixColIndex(key, keyIndex);
  }
  return keyIndex;
}

/** onGetPerformance 等 SDK 列参数：deep80max / BAT68 / tank68he 用固件矩阵列 */
export function resolvePerformanceSdkColIndex(
  key: MatrixKeyLike,
  keyIndex: number,
  keyboardType?: string | null
): number {
  if (isDeep80MaxKeyboardType(keyboardType)) {
    return resolveMatrixColIndex(key, keyIndex);
  }
  if (usesHfd68ShiftGapOriginalCol(keyboardType)) {
    return resolveMatrixColIndex(key, keyIndex);
  }
  return keyIndex;
}

/** 触发/混轴等页：仅 slice 布局需视觉行换算，其余保持 originalRow ?? row */
export function resolveTriggerUiRow(
  item: MatrixKeyLike,
  keyboardType?: string | null
): number | undefined {
  if (usesSliceFirstRowLayout(keyboardType)) {
    return (
      resolveUiMatrixRow(item, keyboardType) ?? item.originalRow ?? item.row
    );
  }
  const r = item.originalRow ?? item.row;
  return r != null && r >= 0 ? r : undefined;
}
